import React from 'react';
import { supabase } from '../lib/supabase';

interface UpdateInfoProps {
  isOpen: boolean;
  onClose: () => void;
  message: {
    title?: string;
    title_description?: string;
    item1?: string;
    item1_description?: string;
    item2?: string;
    item2_description?: string;
    item3?: string;
    item3_description?: string;
    [key: string]: string | undefined;
  };
  userId: string;
}

export const UpdateInfoModal: React.FC<UpdateInfoProps> = ({ isOpen, onClose, message, userId }) => {
  if (!isOpen) return null;

  const handleClose = async () => {
    try {
      // Update the home_message_seen to true
      const { error } = await supabase
        .from('profiles')
        .update({ home_message_seen: true })
        .eq('id', userId);

      if (error) {
        console.error('Error updating message status:', error);
      }

      // Close the modal
      onClose();
    } catch (err) {
      console.error('Error in closing update info modal:', err);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg md:max-w-2xl lg:max-w-3xl p-6 md:p-8 max-h-[90vh] overflow-y-auto mx-auto">
        <div className="space-y-4">
          {/* Header */}
          <div className="text-center">
            {message.title && (
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">{message.title}</h2>
            )}
            {message.title_description && (
              <p className="text-sm text-gray-600 mt-1">{message.title_description}</p>
            )}
          </div>

          {/* Update Items */}
          <div className="space-y-3">
            {/* Item 1 */}
            {message.item1 && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">{message.item1}</h3>
                  {message.item1_description && (
                    <p className="text-sm text-gray-600">{message.item1_description}</p>
                  )}
                </div>
              </div>
            )}

            {/* Item 2 */}
            {message.item2 && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 bg-green-100 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">{message.item2}</h3>
                  {message.item2_description && (
                    <p className="text-sm text-gray-600">{message.item2_description}</p>
                  )}
                </div>
              </div>
            )}

            {/* Item 3 */}
            {message.item3 && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 bg-purple-100 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">{message.item3}</h3>
                  {message.item3_description && (
                    <p className="text-sm text-gray-600">{message.item3_description}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="pt-4 flex justify-center">
            <button
              onClick={handleClose}
              className="inline-flex items-center justify-center h-11 px-8 bg-blue-500 hover:bg-blue-600 text-white font-normal rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150"
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
